import type { FC, ReactElement, ReactNode } from "react"

import {
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Modal,
    Typography
} from "@/3514/components"
import { type TScreenBreakpoints, useDefaultStylesReset, useScreenBreakpoints } from "@/3514/hooks"
import { EModalId } from "@/3514/store/modal"
import { buildCssClassName } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

import { coachingChatOwlComponents as owls } from "../../components/coaching-chat.owls-components"
import { CoachingChatMatrixFeedbackModalProvider as Provider } from "../../context"
import { coachingChatStyles as chatStyles } from "../../styles"

import { CoachingChatMatrixFeedbackModalBody as Body } from "./coaching-chat.matrix-feedback.modal.body.component"

const {
    modals: { matrix: classes }
} = chatStyles

const MODAL_CLASS: string = "matrix-chart-modal"

/**
 * Participant' CoachingChat Matrix Feedback modal.
 * Spec: {@link https://www.figma.com/design/J6qF3Pa6zeK3udtVWLskjd/Participant?node-id=19536-13180&m=dev}
 */
const Container: FC = (): ReactElement => {
    useDefaultStylesReset(MODAL_CLASS)

    const { t }: IUseTranslation = useTranslation()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const titleNode: ReactNode = (
        <div className={classes.modalHeaderNodeContainer}>
            <owls.DefaultLogo fill="blue-500" />
            <div className={classes.modalHeaderNode}>
                <Typography
                    text={t("entities.coachingChat.chat.matrixFeedback.modal.heading")}
                    size={breakpoints.xs || breakpoints.sm ? ETypographySize.Medium : ETypographySize.Heading2}
                    tag={breakpoints.xs || breakpoints.sm ? ETypographyTag.Heading5 : ETypographyTag.Heading2}
                    weight={breakpoints.xs || breakpoints.sm ? ETypographyWeight.Default : ETypographyWeight.Medium}
                    family={breakpoints.xs || breakpoints.sm ? ETypographyFamily.Nunito : ETypographyFamily.DmSerif}
                />
            </div>
        </div>
    )

    return (
        <Modal
            modalId={EModalId.Participant_CoachingChatMatrixFeedback}
            modalBodyClassName={buildCssClassName(classes.modalBody, MODAL_CLASS)}
            title={titleNode}
            withDisabledBodyScroll
            withHideOnOutsideClick
            withHideOnEscapeClick
            withCloseIcon
            withPortal
        >
            <Provider>
                <Body />
            </Provider>
        </Modal>
    )
}

Container.displayName = "CoachingChatMatrixFeedbackModalContainer"

export { Container as CoachingChatMatrixFeedbackModalContainer }
